import React, { useEffect, useRef } from 'react';
import { ToTop, LinkButton } from '../modules';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Campaign = () => {
  const sectionsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    sectionsRef.current.forEach((el) => {
      if (el) {
        gsap.fromTo(
          el,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 85%',
            },
          }
        );
      }
    });
  }, []);

  const addToRefs = (el: HTMLElement | null) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <main className="main bg-[#f3f3f3] min-h-screen font-sans text-[#333]">
      <ToTop />
      
      {/* 1. Hero Section */}
      <section 
        className="relative flex items-center justify-start min-h-[80vh] px-8 md:px-20 py-20"
        style={{
          background: 'linear-gradient(135deg, #6e1e50 0%, #251b3b 100%)',
          color: '#fff',
        }}
      >
        <div className="text-left max-w-2xl z-10 w-full">
          <p className="inline-block px-4 py-1 mb-6 text-sm font-bold tracking-widest bg-white text-[#6e1e50] rounded-full shadow-md">
            浦安・市川で新しくお店を始めるあなたへ。
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8 drop-shadow-lg text-white">
            Web制作ご依頼の方限定！<br/>
            集客に直結する<br/>
            <span>『SNS連携サポート』</span><br/>
            完全無料でプレゼント。
          </h1>
          <p className="text-lg md:text-xl mb-12 text-white font-medium">
            初期費用の負担を抑えつつ、お店のファンを増やす仕組みを作ります。
          </p>
          <LinkButton to="/contact" text="まずは無料でオンライン相談・お見積り" />
        </div>
      </section>

      {/* 2. お悩み共感セクション */}
      <section ref={addToRefs} className="py-24 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[#6e1e50]">
            新規開業時、こんなお悩みありませんか？
          </h2>
          <div className="bg-[#f9f9f9] p-8 rounded-2xl shadow-sm border border-gray-100 mb-12">
            <ul className="space-y-6 text-lg">
              <li className="flex items-start">
                <span className="text-2xl mr-4">💭</span>
                <span>お店の準備で忙しくて、<strong>Webサイトまで手が回らない…</strong></span>
              </li>
              <li className="flex items-start">
                <span className="text-2xl mr-4">💭</span>
                <span>インスタで集客したいけど、<strong>サイトとどう繋げればいいの？</strong></span>
              </li>
              <li className="flex items-start">
                <span className="text-2xl mr-4">💭</span>
                <span>ロゴや名刺も欲しいけど、<strong>別々の業者に頼むのは面倒…</strong></span>
              </li>
            </ul>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-center text-[#251b3b]">
            そのお悩み、地元の <span className="text-[#6e1e50]">budou design</span> が<br className="md:hidden"/>まるっと解決します！
          </p>
        </div>
      </section>

      {/* 3. キャンペーン特典の詳細 */}
      <section ref={addToRefs} className="py-24 px-4 bg-[#f3f3f3]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#6e1e50] font-bold tracking-widest mb-2">SPECIAL BENEFITS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#333]">
              開業応援キャンペーンの<br className="md:hidden"/>3つの無料特典
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* 特典1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:-translate-y-2 transition duration-300">
              <div className="w-16 h-16 bg-[#6e1e50] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto">1</div>
              <h3 className="text-xl font-bold text-center mb-4">Instagramフィード<br/>サイト埋め込み</h3>
              <p className="text-gray-600 leading-relaxed">
                お店のInstagramで投稿した内容が、自動的にWebサイト上にも表示される仕組みを構築。サイトの更新頻度が上がり、お客様に常に最新情報を届けられます。
              </p>
            </div>
            
            {/* 特典2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:-translate-y-2 transition duration-300">
              <div className="w-16 h-16 bg-[#6e1e50] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto">2</div>
              <h3 className="text-xl font-bold text-center mb-4">シェア効果を最大化する<br/>OGP設定</h3>
              <p className="text-gray-600 leading-relaxed">
                LINEやX（旧Twitter）でお店のURLをシェアされた際、魅力的な画像や説明文が自動で表示されるよう設定。SNSからの流入率が劇的にアップします。
              </p>
            </div>
            
            {/* 特典3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:-translate-y-2 transition duration-300">
              <div className="w-16 h-16 bg-[#6e1e50] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto">3</div>
              <h3 className="text-xl font-bold text-center mb-4">初期設定・保守の<br/>徹底サポート</h3>
              <p className="text-gray-600 leading-relaxed">
                ドメイン取得やサーバーの手配など、面倒なIT作業をまるごとお任せ。公開後も、地元密着の強みを活かしてちょっとした修正やご相談に素早く対応します。
              </p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-lg text-gray-600 bg-white inline-block px-8 py-4 rounded-xl shadow-sm border-l-4 border-[#6e1e50]">
              ✨ さらにご希望の方には、<strong>「お店のロゴ」や「名刺」のデザイン</strong>も一括でお引き受け可能です！
            </p>
          </div>
        </div>
      </section>

      {/* 4. 選ばれる理由 (Why budou design?) */}
      <section ref={addToRefs} className="py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#6e1e50] font-bold tracking-widest mb-2">WHY US?</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#333]">
              budou design が選ばれる理由
            </h2>
          </div>

          <div className="space-y-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/2">
                <div className="bg-[#f3f3f3] w-full h-64 rounded-2xl flex items-center justify-center text-6xl shadow-inner">🤝</div>
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4 text-[#6e1e50]">地元密着の圧倒的な安心感</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  浦安・市川エリアのビジネスなら、直接お伺いしてのお打ち合わせも大歓迎です。「顔が見える距離」にいるプロフェッショナルとして、あなたのビジネスに深く寄り添います。
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="md:w-1/2">
                <div className="bg-[#f3f3f3] w-full h-64 rounded-2xl flex items-center justify-center text-6xl shadow-inner">🎨</div>
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4 text-[#6e1e50]">エンジニア×デザイナーの二刀流</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  見た目の美しさ（UI/UX・イラスト）だけでなく、裏側の仕組み（コード・アニメーション）まで、一人のクリエイターが妥協なく一貫して構築。スピードと品質を両立させます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Section */}
      <section ref={addToRefs} className="py-24 px-4 text-center bg-[#251b3b] text-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            あなたのビジネスの第一歩を、<br className="md:hidden"/>一緒に踏み出しましょう。
          </h2>
          <p className="text-lg md:text-xl mb-12 opacity-80 leading-relaxed">
            「まだ開業前で何も決まっていない…」という漠然としたご相談でも大歓迎です。<br/>まずはお気軽にお話を聞かせてください。
          </p>
          <div className="bg-white p-10 rounded-3xl shadow-2xl inline-block w-full max-w-lg">
            <p className="text-[#6e1e50] font-bold text-2xl mb-8">無料オンライン相談・お見積り</p>
            <LinkButton to="/contact" text="今すぐ相談する" />
            <p className="text-sm text-gray-500 mt-6 font-bold">24時間以内にご返信いたします</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Campaign;
